import React, { useEffect, useState } from 'react';

// In-game clock controls of the admin panel's weather tab: shows the game time
// and lets an admin set an hour, step it, or go back to the normal clock. The
// server owns the clock (timeSystem.ts) and answers every change with a fresh
// snapshot, which arrives here as the `time` prop.

export interface ClockTime {
  hour: number;
  minute: number;
  // Distance from the normal clock in minutes, signed
  offsetMinutes: number;
  // True once an admin changed it and until it is reset
  adjusted: boolean;
}

const PRESETS: Array<{ label: string; time: string }> = [
  { label: 'Aube', time: '06:00' },
  { label: 'Midi', time: '12:00' },
  { label: 'Crépuscule', time: '18:00' },
  { label: 'Minuit', time: '00:00' },
];

const STEPS: Array<{ label: string; minutes: number }> = [
  { label: '-1 h', minutes: -60 },
  { label: '-10 min', minutes: -10 },
  { label: '+10 min', minutes: 10 },
  { label: '+1 h', minutes: 60 },
];

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('timeControls sendMessage', payload);
  }
};

const pad = (n: number): string => String(n).padStart(2, '0');

// "+3 h 20 min" / "-45 min", empty for no difference
export const describeOffset = (minutes: number): string => {
  if (!minutes) return '';
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h ? `${h} h` : ''}${h && m ? ' ' : ''}${m ? `${m} min` : ''}`;
};

export const TimeControls: React.FC<{ time: ClockTime }> = ({ time }) => {
  const [hour, setHour] = useState(String(time.hour));
  const [minute, setMinute] = useState(String(time.minute));
  // The snapshot is a photo of one moment; the clock keeps running, so the
  // display adds the minutes elapsed since it was received
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    setReceivedAt(Date.now());
    setNow(Date.now());
  }, [time]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, []);

  const total = (time.hour * 60 + time.minute + Math.floor((now - receivedAt) / 60000)) % 1440;
  const shownHour = Math.floor(total / 60);
  const shownMinute = total % 60;

  const h = Number(hour);
  const m = Number(minute);
  const valid = Number.isInteger(h) && h >= 0 && h <= 23 && Number.isInteger(m) && m >= 0 && m <= 59 && hour !== '' && minute !== '';

  return (
    <div className="admin-panel__time">
      <div className="admin-panel__time-head">
        <div>
          <div className="admin-panel__status-label">Heure du jeu</div>
          <div className="admin-panel__time-clock">{pad(shownHour)}:{pad(shownMinute)}</div>
          <div className="admin-panel__time-note">
            {time.adjusted
              ? `Décalée de ${describeOffset(time.offsetMinutes) || '0 min'} par rapport à l'heure normale`
              : "Heure normale (celle du serveur)"}
          </div>
        </div>
        <button
          type="button"
          className="admin-panel__button"
          disabled={!time.adjusted}
          onClick={() => send({ action: 'timeReset' })}
        >
          Rétablir l'heure normale
        </button>
      </div>

      <div className="admin-panel__time-row">
        {PRESETS.map((p) => (
          <button
            key={p.time}
            type="button"
            className="admin-panel__button"
            onClick={() => send({ action: 'timeSet', target: p.time })}
          >
            {p.label} ({p.time})
          </button>
        ))}
      </div>

      <div className="admin-panel__time-row">
        {STEPS.map((s) => (
          <button
            key={s.label}
            type="button"
            className="admin-panel__button admin-panel__button--secondary"
            onClick={() => send({ action: 'timeShift', target: String(s.minutes) })}
          >
            {s.label}
          </button>
        ))}
        <span className="admin-panel__time-sep" />
        <input
          className="admin-panel__time-input"
          type="number"
          min={0}
          max={23}
          value={hour}
          aria-label="Heure"
          onChange={(e) => setHour(e.target.value)}
        />
        <span>:</span>
        <input
          className="admin-panel__time-input"
          type="number"
          min={0}
          max={59}
          value={minute}
          aria-label="Minutes"
          onChange={(e) => setMinute(e.target.value)}
        />
        <button
          type="button"
          className="admin-panel__button admin-panel__button--primary"
          disabled={!valid}
          onClick={() => send({ action: 'timeSet', target: `${pad(h)}:${pad(m)}` })}
        >
          Régler
        </button>
      </div>
    </div>
  );
};
